import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Device, type Call } from "@twilio/voice-sdk";
import { Phone, PhoneOff, PhoneIncoming, Mic, MicOff } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { TelephonyContext, type CallState, type CallOpts, type SmsArgs, type TelephonyApi } from "./context";
import { functionUrl, authHeaders, fetchVoiceToken } from "./util";

// TelephonyProvider (WS-3 Phase 3a.5) — ONE Twilio Voice Device per authenticated
// desktop session, registered on mount so inbound <Dial><Client>{users.id}> calls
// (from twilio-inbound's routing) actually reach the browser. It also backs the
// outbound API (call/hangup/sendSms/getCallSid) so there is a single Device shared
// by inbound + outbound (two Devices on one identity collide). Token is refreshed
// on tokenWillExpire so the registration survives past the 1h TTL. Renders a global
// incoming/active-call panel. Desktop-only (mounted in Layout); mobile keeps the
// standalone lazy Device via useTelephony's fallback.

type RegState = "connecting" | "registered" | "reconnecting" | "offline" | "error";
type CallInfo = { from: string; leadName?: string };

// Best-effort caller lookup by the inbound number (contact name for the panel).
async function lookupCaller(from: string): Promise<{ leadName?: string }> {
  try {
    const sb = getSupabaseClient();
    const { data } = await sb
      .from("contact_phones")
      .select("contacts:contact_id(first_name,last_name)")
      .eq("number_e164", from)
      .limit(1);
    const c = (data as { contacts?: { first_name?: string; last_name?: string } }[] | null)?.[0]?.contacts;
    const name = c ? `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() : "";
    return { leadName: name || undefined };
  } catch (_) {
    return {};
  }
}

export function TelephonyProvider({ children }: { children: ReactNode }) {
  const deviceRef = useRef<Device | null>(null);
  // outbound
  const outCallRef = useRef<Call | null>(null);
  const callSidRef = useRef<string | null>(null);
  const [callState, setCallState] = useState<CallState>("idle");
  const [error, setError] = useState<string | null>(null);
  // inbound
  const inCallRef = useRef<Call | null>(null);
  const activeCallRef = useRef<Call | null>(null);
  const [incoming, setIncoming] = useState<CallInfo | null>(null);
  const [active, setActive] = useState<CallInfo | null>(null);
  const [muted, setMuted] = useState(false);
  const [reg, setReg] = useState<RegState>("connecting");

  // ---- Device lifecycle: register on mount (authenticated tree), refresh token,
  //      recover on error with backoff, destroy on unmount (logout). ------------
  useEffect(() => {
    let device: Device | null = null;
    let destroyed = false;
    let backoff = 2000;
    let recoverTimer: ReturnType<typeof setTimeout> | undefined;

    const clearInbound = (call: Call) => {
      if (inCallRef.current === call) { inCallRef.current = null; setIncoming(null); }
      if (activeCallRef.current === call) { activeCallRef.current = null; setActive(null); setMuted(false); }
    };

    const handleIncoming = (call: Call) => {
      // Busy: already ringing or on a call → auto-reject the new one, keep the current.
      if (outCallRef.current || activeCallRef.current || inCallRef.current) {
        try { call.reject(); } catch (_) { /* ignore */ }
        return;
      }
      inCallRef.current = call;
      const from = call.parameters?.From ?? "Unknown";
      setIncoming({ from });
      void lookupCaller(from).then((info) => setIncoming((cur) => (cur ? { ...cur, ...info } : cur)));
      // team mode: when another agent accepts, Twilio cancels this leg — clear cleanly.
      call.on("cancel", () => clearInbound(call));
      call.on("reject", () => clearInbound(call));
      call.on("disconnect", () => clearInbound(call));
      call.on("error", () => clearInbound(call));
    };

    const recover = () => {
      if (destroyed || !device) return;
      setReg("reconnecting");
      recoverTimer = setTimeout(async () => {
        if (destroyed || !device) return;
        try {
          device.updateToken(await fetchVoiceToken());
          await device.register();
          backoff = 2000;
        } catch (_) {
          backoff = Math.min(backoff * 2, 30000);
          recover();
        }
      }, backoff);
    };

    (async () => {
      const { data } = await getSupabaseClient().auth.getSession();
      if (!data.session || destroyed) { setReg("offline"); return; }
      let token: string;
      try { token = await fetchVoiceToken(); } catch (_) { setReg("error"); return; }
      if (destroyed) return;
      device = new Device(token, { logLevel: "error" });
      deviceRef.current = device;
      device.on("registered", () => setReg("registered"));
      device.on("unregistered", () => setReg("offline"));
      device.on("error", () => { setReg("error"); recover(); });
      device.on("tokenWillExpire", async () => {
        try { device?.updateToken(await fetchVoiceToken()); } catch (_) { /* error handler recovers */ }
      });
      device.on("incoming", handleIncoming);
      try { await device.register(); } catch (_) { setReg("error"); recover(); }
    })();

    return () => {
      destroyed = true;
      if (recoverTimer) clearTimeout(recoverTimer);
      try { device?.disconnectAll?.(); } catch (_) { /* ignore */ }
      try { device?.unregister?.(); } catch (_) { /* ignore */ }
      try { device?.destroy?.(); } catch (_) { /* ignore */ }
      deviceRef.current = null;
    };
  }, []);

  // ---- Outbound API (identical surface to the legacy useTelephony) ------------
  const call = useCallback(async (to: string, leadId?: string, opts?: CallOpts) => {
    setError(null);
    setCallState("connecting");
    callSidRef.current = null;
    const device = deviceRef.current;
    if (!device) { setError("Phone still connecting — try again in a moment."); setCallState("error"); return; }
    try {
      const c = await device.connect({
        params: { To: to, lead_id: leadId ?? "", agent_user_id: opts?.agentUserId ?? "", session_id: opts?.sessionId ?? "" },
      });
      outCallRef.current = c;
      const grabSid = () => { callSidRef.current = c.parameters?.CallSid ?? callSidRef.current; };
      grabSid();
      c.on("ringing", () => { grabSid(); setCallState("ringing"); });
      c.on("accept", () => { grabSid(); setCallState("open"); });
      c.on("disconnect", () => { setCallState("closed"); outCallRef.current = null; });
      c.on("error", (e: Error) => { setError(e.message); setCallState("error"); outCallRef.current = null; });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setCallState("error");
    }
  }, []);

  const hangup = useCallback(() => {
    outCallRef.current?.disconnect();
    outCallRef.current = null;
    setCallState("idle");
  }, []);

  const getCallSid = useCallback(() => callSidRef.current, []);

  const sendSms = useCallback(async (args: SmsArgs) => {
    const res = await fetch(await functionUrl("outbound-sms"), {
      method: "POST", headers: await authHeaders(), body: JSON.stringify(args),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.detail || data?.error || `send failed (${res.status})`);
    return data;
  }, []);

  // ---- Inbound actions --------------------------------------------------------
  const acceptIncoming = useCallback(() => {
    const c = inCallRef.current;
    if (!c) return;
    try {
      c.accept(); // triggers getUserMedia
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not accept the call");
      return;
    }
    activeCallRef.current = c;
    setActive(incoming);
    setIncoming(null);
    inCallRef.current = null;
    setMuted(false);
    c.on("disconnect", () => { activeCallRef.current = null; setActive(null); setMuted(false); });
    c.on("error", (e: Error) => { setError(e?.message || "Call error"); activeCallRef.current = null; setActive(null); });
  }, [incoming]);

  const rejectIncoming = useCallback(() => {
    try { inCallRef.current?.reject(); } catch (_) { /* ignore */ }
    inCallRef.current = null;
    setIncoming(null);
  }, []);

  const hangupActive = useCallback(() => {
    try { activeCallRef.current?.disconnect(); } catch (_) { /* ignore */ }
    activeCallRef.current = null;
    setActive(null);
    setMuted(false);
  }, []);

  const toggleMute = useCallback(() => {
    const c = activeCallRef.current;
    if (!c) return;
    const m = !muted;
    try { c.mute(m); setMuted(m); } catch (_) { /* ignore */ }
  }, [muted]);

  const api = useMemo<TelephonyApi>(
    () => ({ call, hangup, sendSms, callState, error, getCallSid }),
    [call, hangup, sendSms, callState, error, getCallSid],
  );

  return (
    <TelephonyContext.Provider value={api}>
      {children}
      <TelephonyOverlay
        reg={reg}
        incoming={incoming}
        active={active}
        muted={muted}
        error={error}
        onAccept={acceptIncoming}
        onReject={rejectIncoming}
        onHangup={hangupActive}
        onToggleMute={toggleMute}
        onDismissError={() => setError(null)}
      />
    </TelephonyContext.Provider>
  );
}

// ---- Global incoming / active call panel + registration indicator ------------
function TelephonyOverlay({
  reg, incoming, active, muted, error, onAccept, onReject, onHangup, onToggleMute, onDismissError,
}: {
  reg: RegState; incoming: CallInfo | null; active: CallInfo | null; muted: boolean; error: string | null;
  onAccept: () => void; onReject: () => void; onHangup: () => void; onToggleMute: () => void; onDismissError: () => void;
}) {
  const regLabel: Record<RegState, string> = {
    connecting: "Phone connecting…", registered: "Phone ready", reconnecting: "Phone reconnecting…",
    offline: "Phone offline", error: "Phone error — reconnecting…",
  };
  // Portal to document.body so the panel escapes any ancestor stacking context /
  // overflow / transform (the shadcn SidebarProvider creates positioned containers
  // that otherwise clip or cover a fixed sibling). z-index above app chrome + toasts.
  if (typeof document === "undefined") return null;
  return createPortal(
    <div className="fixed bottom-4 right-4 z-[2147483000] flex flex-col items-end gap-2 pointer-events-none">
      {/* subtle registration indicator (hidden once cleanly registered) */}
      {reg !== "registered" && (
        <Badge variant="secondary" className="pointer-events-auto text-[11px]">{regLabel[reg]}</Badge>
      )}

      {error && (active || incoming) && (
        <Card className="pointer-events-auto w-72 border-red-300">
          <CardContent className="p-2 text-xs text-red-800 flex items-center justify-between gap-2">
            <span>{error}</span>
            <Button size="sm" variant="ghost" onClick={onDismissError}>Dismiss</Button>
          </CardContent>
        </Card>
      )}

      {/* incoming (ringing) */}
      {incoming && !active && (
        <Card className="pointer-events-auto w-72 shadow-lg border-primary/40">
          <CardContent className="p-3 space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <PhoneIncoming className="h-4 w-4 text-primary animate-pulse" /> Incoming call
            </div>
            <div className="text-sm">{incoming.leadName || "Unknown caller"}</div>
            <div className="font-mono text-xs text-muted-foreground">{incoming.from}</div>
            <div className="flex gap-2 pt-1">
              <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={onAccept}>
                <Phone className="mr-1 h-4 w-4" /> Accept
              </Button>
              <Button size="sm" variant="destructive" className="flex-1" onClick={onReject}>
                <PhoneOff className="mr-1 h-4 w-4" /> Decline
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* active */}
      {active && (
        <Card className="pointer-events-auto w-72 shadow-lg border-emerald-400/50">
          <CardContent className="p-3 space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Phone className="h-4 w-4 text-emerald-600" /> On call
            </div>
            <div className="text-sm">{active.leadName || "Unknown caller"}</div>
            <div className="font-mono text-xs text-muted-foreground">{active.from}</div>
            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="outline" className="flex-1" onClick={onToggleMute}>
                {muted ? <MicOff className="mr-1 h-4 w-4" /> : <Mic className="mr-1 h-4 w-4" />}
                {muted ? "Unmute" : "Mute"}
              </Button>
              <Button size="sm" variant="destructive" className="flex-1" onClick={onHangup}>
                <PhoneOff className="mr-1 h-4 w-4" /> Hang up
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>,
    document.body,
  );
}
